// OOXML (SpreadsheetML) part builders for the customer workbook — the XML inside the zip.
//
// This is where every "Excel found unreadable content" risk lives, so the rules that matter are
// spelled out inline: schema-fixed child order in workbook.xml / styles.xml / sheetN.xml, rows and
// cells ascending, fills 0 and 1 reserved, no literal NaN, inline strings instead of a shared-string
// table (valid per ECMA-376 §18.3.1.4 and read by Excel, Google Sheets, Numbers and LibreOffice).
//
// Formulas are expected to use direct cross-sheet references (`Calculator!$C$11`). Workbook defined
// names are supported but should be treated as Name Box navigation aids only: an openpyxl-written
// workbook that leaned on them is exactly what rendered as `#NAME?` in Google Sheets, and Numbers
// drops names entirely.

/* ------------------------------------------------------------------ escaping */

// Characters XML 1.0 forbids outright; stripped rather than escaped (they have no escape form).
const ILLEGAL_XML = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;

/** Escape text for element content and attribute values alike (over-escaping is harmless). */
export function escXml(s: string): string {
  return s
    .replace(ILLEGAL_XML, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/* ------------------------------------------------------------------ references */

export const MAX_COL = 16384;
export const MAX_ROW = 1048576;

/** 1 → A, 26 → Z, 27 → AA, 16384 → XFD. */
export function colLetter(col: number): string {
  if (!Number.isInteger(col) || col < 1 || col > MAX_COL) throw new Error(`column out of range: ${col}`);
  let s = '';
  for (let n = col; n > 0; ) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = (n - 1 - m) / 26;
  }
  return s;
}

function assertRow(row: number): void {
  if (!Number.isInteger(row) || row < 1 || row > MAX_ROW) throw new Error(`row out of range: ${row}`);
}

/** 1-based row/col → 'C31'. */
export function cellRef(row: number, col: number): string {
  assertRow(row);
  return `${colLetter(col)}${row}`;
}

/** 1-based row/col → '$C$31'. */
export function absRef(row: number, col: number): string {
  assertRow(row);
  return `$${colLetter(col)}$${row}`;
}

/** Quote a sheet name for use in a formula unless it is a plain identifier that can't be mistaken for a ref. */
export function quoteSheetName(name: string): string {
  const plain = /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && !/^[A-Za-z]{1,3}\d+$/.test(name) && !/^[RC]\d*$/i.test(name);
  return plain ? name : `'${name.replace(/'/g, "''")}'`;
}

/** 'Calculator!$C$31' (sheet quoted when needed). */
export function sheetAbsRef(sheet: string, row: number, col: number): string {
  return `${quoteSheetName(sheet)}!${absRef(row, col)}`;
}

/* ------------------------------------------------------------------ name rules */

const SHEET_NAME_FORBIDDEN = /[\\/?*[\]:]/;

/** Excel's sheet-name rules; violations open a repair prompt (Sheets silently renames, breaking refs). */
export function assertSheetName(name: string): void {
  if (!name) throw new Error('sheet name is empty');
  if (name.length > 31) throw new Error(`sheet name longer than 31 chars: ${name}`);
  if (SHEET_NAME_FORBIDDEN.test(name)) throw new Error(`sheet name contains a forbidden character: ${name}`);
  if (name.startsWith("'") || name.endsWith("'")) throw new Error(`sheet name may not start or end with an apostrophe: ${name}`);
  if (name.toLowerCase() === 'history') throw new Error('sheet name "History" is reserved by Excel');
}

/**
 * Defined-name rules shared by Excel and Google Sheets: identifier characters, not a cell-reference
 * lookalike (`li1` is column LI row 1; `C31` is a cell), not R1C1, not a built-in `_xlnm.` name.
 */
export function assertDefinedName(name: string): void {
  if (!/^[A-Za-z_\\][A-Za-z0-9_.\\]*$/.test(name)) throw new Error(`invalid defined name: ${name}`);
  if (name.length > 255) throw new Error(`defined name longer than 255 chars: ${name}`);
  if (/^[A-Za-z]{1,3}\d+$/.test(name)) throw new Error(`defined name looks like a cell reference: ${name}`);
  if (/^[RC]\d*$/i.test(name) || /^R\d*C\d*$/i.test(name)) throw new Error(`defined name looks like an R1C1 reference: ${name}`);
  if (/^_xlnm\./i.test(name)) throw new Error(`defined name uses the reserved _xlnm. prefix: ${name}`);
  if (/^(true|false)$/i.test(name)) throw new Error(`defined name is a boolean literal: ${name}`);
}

/* ------------------------------------------------------------------ styles */

/** Visual roles a cell can take. Order is load-bearing: it fixes the cellXfs index arithmetic. */
export const STYLE_KINDS = [
  'plain',
  'title',
  'header',
  'section',
  'input',
  'inputAssumed',
  'benchmark',
  'intermediate',
  'sectionOutput',
  'total',
  'note',
] as const;
export type StyleKind = (typeof STYLE_KINDS)[number];

/** Number formats. Order is load-bearing for the same reason. */
export const NUM_FMTS = ['general', 'count', 'pct', 'money', 'money2', 'num1', 'hrs', 'min'] as const;
export type NumFmt = (typeof NUM_FMTS)[number];

// numFmtId per NumFmt. 0 and 3 are Excel built-ins (General, #,##0) and must not be redeclared;
// custom ids start at 164. `pct` is custom (`0.##%`) so an SE's 4.05% survives instead of rounding
// to 4% under the built-in `0%`.
const NUM_FMT_IDS: Record<NumFmt, number> = {
  general: 0,
  count: 3,
  pct: 164,
  money: 165,
  money2: 166,
  num1: 167,
  hrs: 168,
  min: 169,
};
const CUSTOM_NUM_FMTS: Array<[number, string]> = [
  [164, '0.##%'],
  [165, '"$"#,##0'],
  [166, '"$"#,##0.00'],
  [167, '#,##0.0'],
  [168, '#,##0 "hrs"'],
  [169, '0 "min"'],
];

function font(o: { b?: boolean; i?: boolean; sz: number; color: string }): string {
  return `<font>${o.b ? '<b/>' : ''}${o.i ? '<i/>' : ''}<sz val="${o.sz}"/><color rgb="${o.color}"/><name val="Calibri"/><family val="2"/></font>`;
}

// Fonts: index → XML. Child order (b, i, sz, color, name, family) is the order Excel itself writes.
const FONTS = [
  font({ sz: 11, color: 'FF000000' }), // 0 plain
  font({ b: true, sz: 11, color: 'FFFFFFFF' }), // 1 header (on navy)
  font({ sz: 11, color: 'FF1D4ED8' }), // 2 input (blue)
  font({ i: true, sz: 11, color: 'FF2563EB' }), // 3 benchmark (blue italic)
  font({ b: true, sz: 11, color: 'FF000000' }), // 4 bold black
  font({ b: true, sz: 11, color: 'FFFFFFFF' }), // 5 total (on dark green)
  font({ sz: 10, color: 'FF6B7280' }), // 6 note (grey)
  font({ b: true, sz: 14, color: 'FF1F3A8A' }), // 7 title (navy)
];

function solid(rgb: string): string {
  return `<fill><patternFill patternType="solid"><fgColor rgb="${rgb}"/><bgColor indexed="64"/></patternFill></fill>`;
}

// Fills: indices 0 (none) and 1 (gray125) are reserved by Excel and must come first, in that order.
// For a solid fill the visible colour is fgColor.
const FILLS = [
  '<fill><patternFill patternType="none"/></fill>',
  '<fill><patternFill patternType="gray125"/></fill>',
  solid('FF1F3A8A'), // 2 header navy
  solid('FFDBEAFE'), // 3 input light blue
  solid('FFDCFCE7'), // 4 section output light green
  solid('FF166534'), // 5 total dark green
  solid('FFFFF4CC'), // 6 assumed input light amber
  solid('FFF3F4F6'), // 7 section band light grey
];

const KIND_FONT_FILL: Record<StyleKind, [font: number, fill: number]> = {
  plain: [0, 0],
  title: [7, 0],
  header: [1, 2],
  section: [4, 7],
  input: [2, 3],
  inputAssumed: [2, 6],
  benchmark: [3, 0],
  intermediate: [4, 0],
  sectionOutput: [4, 4],
  total: [5, 5],
  note: [6, 0],
};

/**
 * The cellXfs index a cell uses via `s="…"`. Layout: `[wrap ? 1 : 0][kind][numFmt]`, so index 0 is
 * plain/general/no-wrap — which is what Excel requires the first xf to be.
 */
export function xfIndex(kind: StyleKind = 'plain', nf: NumFmt = 'general', wrap = false): number {
  const k = STYLE_KINDS.indexOf(kind);
  const f = NUM_FMTS.indexOf(nf);
  if (k < 0) throw new Error(`unknown style kind: ${kind}`);
  if (f < 0) throw new Error(`unknown number format: ${nf}`);
  return (wrap ? STYLE_KINDS.length * NUM_FMTS.length : 0) + k * NUM_FMTS.length + f;
}

const XML_DECL = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const NS_MAIN = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

/** styles.xml. Child order is schema-fixed: numFmts, fonts, fills, borders, cellStyleXfs, cellXfs, cellStyles. */
export function stylesXml(): string {
  const xfs: string[] = [];
  for (const wrap of [false, true]) {
    for (const kind of STYLE_KINDS) {
      const [fontId, fillId] = KIND_FONT_FILL[kind];
      for (const nf of NUM_FMTS) {
        const numFmtId = NUM_FMT_IDS[nf];
        const attrs = [
          `numFmtId="${numFmtId}"`,
          `fontId="${fontId}"`,
          `fillId="${fillId}"`,
          'borderId="0"',
          'xfId="0"',
          numFmtId !== 0 ? 'applyNumberFormat="1"' : '',
          fontId !== 0 ? 'applyFont="1"' : '',
          fillId !== 0 ? 'applyFill="1"' : '',
          wrap ? 'applyAlignment="1"' : '',
        ]
          .filter(Boolean)
          .join(' ');
        xfs.push(wrap ? `<xf ${attrs}><alignment wrapText="1" vertical="top"/></xf>` : `<xf ${attrs}/>`);
      }
    }
  }
  return (
    XML_DECL +
    `<styleSheet xmlns="${NS_MAIN}">` +
    `<numFmts count="${CUSTOM_NUM_FMTS.length}">` +
    CUSTOM_NUM_FMTS.map(([id, code]) => `<numFmt numFmtId="${id}" formatCode="${escXml(code)}"/>`).join('') +
    '</numFmts>' +
    `<fonts count="${FONTS.length}">${FONTS.join('')}</fonts>` +
    `<fills count="${FILLS.length}">${FILLS.join('')}</fills>` +
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    `<cellXfs count="${xfs.length}">${xfs.join('')}</cellXfs>` +
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
    '</styleSheet>'
  );
}

/* ------------------------------------------------------------------ sheet model */

export interface Cell {
  /** Literal value, or the cached result when `f` is set. */
  v?: number | string;
  /** Formula without the leading '='. */
  f?: string;
  s?: StyleKind;
  nf?: NumFmt;
  wrap?: boolean;
}

export interface ListValidation {
  /** Space-separated cell refs or ranges, e.g. 'B9' or 'B9 B10'. */
  sqref: string;
  list: string[];
}

export interface SheetSpec {
  name: string;
  /** rows[r][c] — 0-based, sparse (null/undefined cells and rows are skipped). */
  rows: ReadonlyArray<ReadonlyArray<Cell | null | undefined> | null | undefined>;
  /** Column widths in character units, by 0-based column index. */
  cols?: ReadonlyArray<number | undefined>;
  freeze?: { rows: number; cols: number };
  validations?: ListValidation[];
}

export interface DefinedName {
  name: string;
  sheet: string;
  /** 1-based. */
  row: number;
  col: number;
}

function needsPreserve(s: string): boolean {
  return /^\s|\s$|[\n\r\t]/.test(s);
}

/** One `<c>` element. Never emits a literal NaN — non-finite numbers become the #N/A error cell. */
export function cellXml(row: number, col: number, cell: Cell): string {
  const r = cellRef(row, col);
  const sIdx = cell.s || cell.nf || cell.wrap ? xfIndex(cell.s ?? 'plain', cell.nf ?? 'general', !!cell.wrap) : 0;
  const s = sIdx ? ` s="${sIdx}"` : '';
  if (cell.f !== undefined) {
    const f = `<f>${escXml(cell.f)}</f>`;
    if (typeof cell.v === 'string') return `<c r="${r}"${s} t="str">${f}<v>${escXml(cell.v)}</v></c>`;
    if (typeof cell.v === 'number') {
      if (!Number.isFinite(cell.v)) return `<c r="${r}"${s} t="e">${f}<v>#N/A</v></c>`;
      return `<c r="${r}"${s}>${f}<v>${String(cell.v)}</v></c>`;
    }
    return `<c r="${r}"${s}>${f}</c>`;
  }
  if (typeof cell.v === 'string') {
    const t = needsPreserve(cell.v) ? `<t xml:space="preserve">${escXml(cell.v)}</t>` : `<t>${escXml(cell.v)}</t>`;
    return `<c r="${r}"${s} t="inlineStr"><is>${t}</is></c>`;
  }
  if (typeof cell.v === 'number') {
    if (!Number.isFinite(cell.v)) return `<c r="${r}"${s} t="e"><v>#N/A</v></c>`;
    return `<c r="${r}"${s}><v>${String(cell.v)}</v></c>`;
  }
  return `<c r="${r}"${s}/>`;
}

function paneXml(freeze: { rows: number; cols: number } | undefined): string {
  if (!freeze || (freeze.rows <= 0 && freeze.cols <= 0)) return '';
  const { rows, cols } = freeze;
  const topLeft = cellRef(rows + 1, cols + 1);
  if (rows > 0 && cols > 0) {
    return (
      `<pane xSplit="${cols}" ySplit="${rows}" topLeftCell="${topLeft}" activePane="bottomRight" state="frozen"/>` +
      '<selection pane="topRight"/><selection pane="bottomLeft"/>' +
      `<selection pane="bottomRight" activeCell="${topLeft}" sqref="${topLeft}"/>`
    );
  }
  if (rows > 0) {
    return (
      `<pane ySplit="${rows}" topLeftCell="${topLeft}" activePane="bottomLeft" state="frozen"/>` +
      `<selection pane="bottomLeft" activeCell="${topLeft}" sqref="${topLeft}"/>`
    );
  }
  return (
    `<pane xSplit="${cols}" topLeftCell="${topLeft}" activePane="topRight" state="frozen"/>` +
    `<selection pane="topRight" activeCell="${topLeft}" sqref="${topLeft}"/>`
  );
}

/**
 * One worksheet part. Child order is schema-fixed: dimension, sheetViews, sheetFormatPr, cols,
 * sheetData, dataValidations, pageMargins. Rows ascend; cells within a row ascend.
 */
export function sheetXml(spec: SheetSpec, tabSelected: boolean): string {
  let maxRow = 0;
  let maxCol = 0;
  const rowsXml: string[] = [];
  spec.rows.forEach((row, ri) => {
    if (!row) return;
    const cells: string[] = [];
    row.forEach((cell, ci) => {
      if (!cell) return;
      cells.push(cellXml(ri + 1, ci + 1, cell));
      if (ci + 1 > maxCol) maxCol = ci + 1;
    });
    if (!cells.length) return;
    maxRow = ri + 1;
    rowsXml.push(`<row r="${ri + 1}">${cells.join('')}</row>`);
  });

  const dimension = maxRow ? `<dimension ref="A1:${cellRef(maxRow, maxCol)}"/>` : '';
  const sheetView = `<sheetViews><sheetView workbookViewId="0"${tabSelected ? ' tabSelected="1"' : ''}>${paneXml(spec.freeze)}</sheetView></sheetViews>`;
  const cols = (spec.cols ?? [])
    .map((w, i) => (w === undefined ? '' : `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`))
    .join('');
  const colsXml = cols ? `<cols>${cols}</cols>` : '';
  const validations = spec.validations?.length
    ? `<dataValidations count="${spec.validations.length}">` +
      spec.validations
        .map(
          (v) =>
            `<dataValidation type="list" allowBlank="1" showDropDown="0" showInputMessage="1" showErrorMessage="1" sqref="${escXml(v.sqref)}">` +
            `<formula1>${escXml(`"${v.list.join(',')}"`)}</formula1></dataValidation>`,
        )
        .join('') +
      '</dataValidations>'
    : '';

  return (
    XML_DECL +
    `<worksheet xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">` +
    dimension +
    sheetView +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    colsXml +
    `<sheetData>${rowsXml.join('')}</sheetData>` +
    validations +
    '<pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>' +
    '</worksheet>'
  );
}

/* ------------------------------------------------------------------ package parts */

export function contentTypesXml(sheetCount: number): string {
  const overrides = Array.from(
    { length: sheetCount },
    (_, i) =>
      `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
  ).join('');
  return (
    XML_DECL +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    overrides +
    '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>' +
    '</Types>'
  );
}

export function rootRelsXml(): string {
  return (
    XML_DECL +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
    '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>' +
    '</Relationships>'
  );
}

export function corePropsXml(title: string, now: Date): string {
  const stamp = now.toISOString().replace(/\.\d{3}Z$/, 'Z');
  return (
    XML_DECL +
    '<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" ' +
    'xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" ' +
    'xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">' +
    `<dc:title>${escXml(title)}</dc:title>` +
    '<dc:creator>Clay Value Calculator</dc:creator>' +
    `<dcterms:created xsi:type="dcterms:W3CDTF">${stamp}</dcterms:created>` +
    `<dcterms:modified xsi:type="dcterms:W3CDTF">${stamp}</dcterms:modified>` +
    '</cp:coreProperties>'
  );
}

/**
 * workbook.xml. Child order is schema-fixed: workbookPr, bookViews, sheets, definedNames (omitted
 * entirely when empty — an empty element is a schema violation), calcPr. `fullCalcOnLoad` makes
 * Excel recompute on open even when cached values are stale; Sheets and Numbers always recalc.
 */
export function workbookXml(sheetNames: string[], names: DefinedName[]): string {
  const sheets = sheetNames
    .map((n, i) => `<sheet name="${escXml(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join('');
  const defined = names.length
    ? '<definedNames>' +
      names.map((d) => `<definedName name="${escXml(d.name)}">${escXml(sheetAbsRef(d.sheet, d.row, d.col))}</definedName>`).join('') +
      '</definedNames>'
    : '';
  return (
    XML_DECL +
    `<workbook xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">` +
    '<workbookPr/>' +
    '<bookViews><workbookView activeTab="0"/></bookViews>' +
    `<sheets>${sheets}</sheets>` +
    defined +
    '<calcPr calcId="191029" fullCalcOnLoad="1"/>' +
    '</workbook>'
  );
}

export function workbookRelsXml(sheetCount: number): string {
  const rels = Array.from(
    { length: sheetCount },
    (_, i) =>
      `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
  ).join('');
  return (
    XML_DECL +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    rels +
    `<Relationship Id="rId${sheetCount + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    '</Relationships>'
  );
}
